// src/components/FilterBar.js
import React, { useEffect, useState } from 'react';

const GROUPS = [{ key: 'upper', label: 'Upper Body' }, { key: 'lower', label: 'Lower Body' }, { key: 'core', label: 'Core' }, { key: 'stretch', label: 'Stretch' }];

// ── Mobile collapse breakpoint ──
// Matches CLAUDE.md responsive convention (768px). Below this width the filter
// bar collapses to a single-row toolbar (Filters · N · Sort) with active filter
// chips visible inline. Above this width the existing wrapped-pill layout fits
// comfortably and is rendered without any toggle. A separate ≤600px CSS rule
// in index.css forces 16px padding via `!important`; that rule continues to
// apply naturally and isn't affected by this component's logic.
const MOBILE_BREAKPOINT_QUERY = '(max-width: 768px)';

function FilterBar({ lengthFilter, setLengthFilter, groupFilter, setGroupFilter, sortBy, setSortBy, availableDurations, completedFilter, setCompletedFilter, completedCount, favoritedFilter, setFavoritedFilter, favoritedCount, easeInFilter, setEaseInFilter, easeInCount, strongPlusFilter, setStrongPlusFilter, strongPlusCount }) {
    const toggle = (arr, setArr, val) => setArr((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
    const durations = availableDurations && availableDurations.length > 0 ? availableDurations : [1, 5, 10, 20];

    // ── Mobile collapse state (2026-05-04) ──
    // Default: expanded on desktop, collapsed on mobile. matchMedia keeps `isMobile`
    // in sync with viewport changes (orientation flip, browser window resize) so the
    // toggle row appears/disappears correctly. `isExpanded` is intentionally NOT
    // reset on viewport crossing — we don't want to hide the user's visible filters
    // mid-session by collapsing on a desktop→mobile resize. The user can collapse
    // explicitly via the toggle.
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            ? window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches
            : false
    );
    const [isExpanded, setIsExpanded] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
        return !window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches;
    });
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        const mql = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
        const onChange = (e) => setIsMobile(e.matches);
        // Older Safari (<14) only supports the deprecated addListener API.
        if (mql.addEventListener) mql.addEventListener('change', onChange);
        else if (mql.addListener) mql.addListener(onChange);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', onChange);
            else if (mql.removeListener) mql.removeListener(onChange);
        };
    }, []);

    const pillStyle = (active) => ({
        padding: '8px 18px',
        borderRadius: 50,
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        background: active ? 'var(--mom-filter-pill-active-bg)' : 'var(--mom-filter-pill-bg)',
        color: active ? 'var(--mom-filter-pill-active-text)' : 'var(--mom-filter-pill-text)',
        border: 'none',
        boxShadow: active ? '0 2px 8px color-mix(in srgb, var(--mom-filter-pill-active-bg) 25%, transparent)' : 'none',
        transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    });

    // Active filter inventory — drives the count badge on the Filters button and
    // the removable-chip preview shown when the bar is collapsed on mobile.
    const activeCount =
        lengthFilter.length +
        groupFilter.length +
        (completedFilter ? 1 : 0) +
        (favoritedFilter ? 1 : 0) +
        (easeInFilter ? 1 : 0) +
        (strongPlusFilter ? 1 : 0);
    const hasActiveFilters = activeCount > 0;
    const activeChips = [
        ...lengthFilter.map((d) => ({ key: `len-${d}`, label: `${d} min`, remove: () => toggle(lengthFilter, setLengthFilter, d) })),
        ...groupFilter.map((g) => ({ key: `grp-${g}`, label: GROUPS.find((x) => x.key === g)?.label || g, remove: () => toggle(groupFilter, setGroupFilter, g) })),
        ...(completedFilter === 'completed' ? [{ key: 'completed', label: 'Completed', remove: () => setCompletedFilter(null) }] : []),
        ...(favoritedFilter === 'favorited' ? [{ key: 'favorited', label: 'Favorited', remove: () => setFavoritedFilter(null) }] : []),
        ...(easeInFilter === 'easein' ? [{ key: 'easein', label: 'Ease In', remove: () => setEaseInFilter(null) }] : []),
        ...(strongPlusFilter === 'strongplus' ? [{ key: 'strongplus', label: 'Strong+', remove: () => setStrongPlusFilter(null) }] : []),
    ];

    const hasStatusSection = (setCompletedFilter && completedCount > 0) || (setFavoritedFilter && favoritedCount > 0) || (setEaseInFilter && easeInCount > 0) || (setStrongPlusFilter && strongPlusCount > 0);

    const clearAll = () => {
        setLengthFilter([]);
        setGroupFilter([]);
        if (setCompletedFilter) setCompletedFilter(null);
        if (setFavoritedFilter) setFavoritedFilter(null);
        if (setEaseInFilter) setEaseInFilter(null);
        if (setStrongPlusFilter) setStrongPlusFilter(null);
    };

    // Card padding: tighter on mobile (601-768px range, since ≤600px is forced to
    // 16px by the existing CSS rule). Desktop uses the original 24px 28px.
    const cardPadding = isMobile ? '14px 16px' : '24px 28px';

    // Sort options — single source of truth so mobile (in toggle row) and desktop
    // (in expanded panel) stay in sync.
    const sortOptions = (
        <>
            <option value="newest">Newest first</option>
            <option value="length-asc">Shortest first</option>
            <option value="length-desc">Longest first</option>
            <option value="alpha">A–Z</option>
        </>
    );

    return (
        <div className="mom-filter-bar mom-card" style={{ padding: cardPadding, marginBottom: 32, borderRadius: 20 }}>
            {/* ── Mobile-only toggle row ─────────────────────────────────────
                Single-row toolbar: Filters button (with active count) + Sort
                dropdown. ≥768px users skip this entirely and get the always-on
                wrapped-pill layout below. */}
            {isMobile && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
                    <button
                        type="button"
                        aria-expanded={isExpanded}
                        aria-controls="mom-filter-panel"
                        onClick={() => setIsExpanded((v) => !v)}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                            padding: '8px 14px',
                            borderRadius: 12,
                            background: 'rgba(255,255,255,0.65)',
                            backdropFilter: 'blur(12px) saturate(1.4)',
                            WebkitBackdropFilter: 'blur(12px) saturate(1.4)',
                            border: '1px solid rgba(255,255,255,0.7)',
                            fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                            color: 'var(--mom-charcoal)', cursor: 'pointer',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <line x1="4" y1="6" x2="20" y2="6" />
                            <line x1="7" y1="12" x2="17" y2="12" />
                            <line x1="10" y1="18" x2="14" y2="18" />
                        </svg>
                        <span>Filters{hasActiveFilters ? ` · ${activeCount}` : ''}</span>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                            style={{ transform: `rotate(${isExpanded ? 180 : 0}deg)`, transition: 'transform 0.2s ease' }}>
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="mom-input"
                        aria-label="Sort"
                        style={{ padding: '8px 12px', borderRadius: 12, fontSize: 13, cursor: 'pointer', maxWidth: 170, minWidth: 0 }}>
                        {sortOptions}
                    </select>
                </div>
            )}

            {/* ── Mobile-only: removable active-filter chip preview when collapsed ──
                Lets the user see and remove individual filters at a glance without
                having to expand the panel. Each chip's whole surface is the remove
                affordance (the ✕ is the visual cue). */}
            {isMobile && !isExpanded && hasActiveFilters && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                    {activeChips.map((chip) => (
                        <button key={chip.key} type="button" onClick={chip.remove}
                            aria-label={`Remove ${chip.label} filter`}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '5px 7px 5px 12px',
                                borderRadius: 999,
                                background: 'var(--mom-filter-pill-active-bg)',
                                color: 'var(--mom-filter-pill-active-text)',
                                fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600,
                                border: 'none', cursor: 'pointer',
                                boxShadow: '0 1px 4px color-mix(in srgb, var(--mom-filter-pill-active-bg) 20%, transparent)',
                            }}>
                            {chip.label}
                            <span aria-hidden="true" style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: 16, height: 16, borderRadius: '50%',
                                background: 'rgba(255,255,255,0.28)',
                                fontSize: 10, lineHeight: 1, fontWeight: 700,
                            }}>✕</span>
                        </button>
                    ))}
                </div>
            )}

            {/* ── Filter sections ──
                Desktop: always rendered (no toggle). Mobile: rendered only when
                expanded. fadeUp animation runs only on mobile expansion to soften
                the panel's appearance; desktop renders without animation since
                it's always there.
                Sort lives in the toggle row on mobile, so it's only rendered
                here for desktop. */}
            {(!isMobile || isExpanded) && (
                <div
                    id="mom-filter-panel"
                    style={{
                        display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-start',
                        position: 'relative', zIndex: 2,
                        marginTop: isMobile ? 14 : 0,
                        animation: isMobile && isExpanded ? 'fadeUp 0.2s ease' : 'none',
                    }}>
                    <div>
                        <label className="mom-section-label" style={{ marginBottom: 10, display: 'block' }}>Duration</label>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {durations.map((l) => (
                                <button key={l} className="mom-btn" onClick={() => toggle(lengthFilter, setLengthFilter, l)}
                                    style={pillStyle(lengthFilter.includes(l))}>
                                    {l} min
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="mom-section-label" style={{ marginBottom: 10, display: 'block' }}>Muscle Group</label>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {GROUPS.map((g) => (
                                <button key={g.key} className="mom-btn" onClick={() => toggle(groupFilter, setGroupFilter, g.key)}
                                    style={pillStyle(groupFilter.includes(g.key))}>
                                    {g.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {hasStatusSection && (
                        <div>
                            <label className="mom-section-label" style={{ marginBottom: 10, display: 'block' }}>Status</label>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {setCompletedFilter && completedCount > 0 && (
                                    <button className="mom-btn" onClick={() => setCompletedFilter(completedFilter === 'completed' ? null : 'completed')}
                                        style={pillStyle(completedFilter === 'completed')}>
                                        Completed
                                    </button>
                                )}
                                {setFavoritedFilter && favoritedCount > 0 && (
                                    <button className="mom-btn" onClick={() => setFavoritedFilter(favoritedFilter === 'favorited' ? null : 'favorited')}
                                        style={pillStyle(favoritedFilter === 'favorited')}>
                                        Favorited
                                    </button>
                                )}
                                {setEaseInFilter && easeInCount > 0 && (
                                    <button className="mom-btn" onClick={() => setEaseInFilter(easeInFilter === 'easein' ? null : 'easein')}
                                        style={pillStyle(easeInFilter === 'easein')}>
                                        Ease In
                                    </button>
                                )}
                                {setStrongPlusFilter && strongPlusCount > 0 && (
                                    <button className="mom-btn" onClick={() => setStrongPlusFilter(strongPlusFilter === 'strongplus' ? null : 'strongplus')}
                                        style={pillStyle(strongPlusFilter === 'strongplus')}>
                                        Strong+
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    {!isMobile && (
                        <div>
                            <label className="mom-section-label" style={{ marginBottom: 10, display: 'block' }}>Sort</label>
                            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="mom-input" style={{ padding: '8px 16px', borderRadius: 12, fontSize: 13, cursor: 'pointer', width: 'auto' }}>
                                {sortOptions}
                            </select>
                        </div>
                    )}
                    {hasActiveFilters && (
                        <button className="mom-btn mom-btn-ghost" onClick={clearAll}
                            style={{ alignSelf: isMobile ? 'flex-start' : 'flex-end', color: 'var(--mom-link)', fontSize: 13, fontWeight: 600 }}>
                            Clear filters
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default FilterBar;
